import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { Feature, FeatureCollection } from 'geojson';
import { BRAND } from './lib/brand';
import { navBasemapStyle } from './lib/basemap';
import { useGps } from './lib/gps';
import { isCarPlayHost } from './lib/locationSource';
import { useSettings } from './lib/settings';
import { bearingDeg, distanceM, formatDistance, formatTotalDistance } from './lib/geo';
import { loadCourse } from './lib/courses';
import { loadCartPaths } from './lib/cartpaths';
import { buildGraph } from './lib/routing';
import { planJourney, type Journey, type TravelMode } from './lib/journey';
import { DEMO_JOURNEY, DEMO_DESTINATION, DEMO_CAPTURES } from './lib/demoRoute';
import { upcoming } from './lib/maneuvers';
import { loadDirectory, nearbyCourses, searchDirectory, STATE_NAMES, type Directory, type DirectoryCourse } from './lib/directory';
// setManual belongs to the hole arrows, which are gone from this screen. The
// manual override still exists in the detector and is still driven from
// /search and from the widget's own hole picker.
import { initialState, step as detectStep, type DetectorState } from './lib/holeDetector';
import { registerCarPlayCommands } from './lib/carplayBridge';
import { CORRIDOR_WIDTH_M, corridorMask, holeSpine } from './lib/shapes';
import { speak, stopSpeaking, unlockVoice, voiceSupported } from './lib/voice';
import { compassSupported, requestCompass, smoothHeading, watchCompass } from './lib/compass';
import { dictationAvailable, startDictation, type Dictation } from './lib/dictation';
import { createPlaceSearch, type Place } from './lib/places';
import { pushYardage, widgetError, widgetPushCount } from './lib/yardageWidget';
import type { Course, Hole, LngLat } from './lib/types';
import { ManeuverIcon } from './components/ManeuverIcon';

/**
 * The navigation screen.
 *
 * Laid out the way every driver already understands: the map is the app, the
 * controls float on it, and everything else lives in a sheet you pull up. That
 * is not imitation for its own sake — a golfer glancing down on a moving buggy
 * has no attention to spend learning a new arrangement, and the conventions
 * exist because they survived contact with real driving.
 *
 * What is different is the network underneath. This routes over cart paths and
 * estate roads as one continuous journey, which is the thing no other
 * navigation app does, and it keeps the yardage to the green as a readout on
 * the route rather than as the point of the app.
 */

type Theme = 'dark' | 'light';
type Destination = { name: string; pos: LngLat; kind: 'course' | 'hole' | 'place' };

const RECENTS_KEY = 'fairway.recents.v1';
const THEME_KEY = 'fairway.theme.v1';
const ON_COURSE_M = 5000;
/*
 * How far away a bundled course can be and still be called "nearest".
 *
 * The directory is Australian and was sorted purely by distance, so opening the
 * app in Arizona listed Exmouth and Karratha — Western Australia, 12,000km
 * away — under the heading NEAREST. Beyond this the honest answer is that we
 * have nothing local, and the server's worldwide lookup fills the gap.
 */
const NEAREST_MAX_M = 250_000;

/** The colour every phone owner already reads as "me". Matches the route. */
const ME_BLUE = '#2f7fff';

/**
 * How long after the golfer stops touching the map before it follows again.
 *
 * Follow-mode used to be a one-way door: one gesture turned it off and the
 * only way back was a small Recentre button. On a buggy that is the wrong
 * default — the golfer looks around, puts the phone down, and the map is
 * still frozen where they left it three holes ago.
 */
const FOLLOW_RESUME_MS = 12_000;
/** True inside the CarPlay web view (body.carplay-host). */
const CAR = isCarPlayHost();

function loadRecents(): Destination[] {
  try {
    return JSON.parse(localStorage.getItem(RECENTS_KEY) ?? '[]') as Destination[];
  } catch {
    return [];
  }
}

export default function NavApp() {
  const { settings, update } = useSettings();
  // Demo / screenshot drive: ?sim drops a simulated golfer on a real, fully
  // cart-path-mapped course (The Palms, Sanctuary Cove — 54 mapped paths) so the
  // genuine turn-by-turn routing and maneuvers can be seen without being on the
  // course. The route and EVERY maneuver are real, computed live from OSM cart
  // paths — only the GPS position is simulated, exactly as it behaves for a real
  // golfer standing there.
  const SIM = typeof location !== 'undefined' && new URLSearchParams(location.search).has('sim');
  // ?demo: a scripted, real-street-name drive (M1 Pacific Motorway -> Exit 60 ->
  // Gold Coast hinterland -> Tamborine Mountain) used only to illustrate the
  // turn-by-turn UI for review — real motorway exit and rural turns, deliberately
  // nowhere near any private address. ?at=exit|nowhere|motorway picks the frame.
  const demoParams = typeof location !== 'undefined' ? new URLSearchParams(location.search) : new URLSearchParams();
  const DEMO = demoParams.has('demo');
  const demoAt = demoParams.get('at') || 'exit';
  const [simPos, setSimPos] = useState<LngLat | null>(
    DEMO ? DEMO_CAPTURES[demoAt] ?? DEMO_CAPTURES.exit : SIM ? [153.36196, -27.85828] : null,
  );
  const { fix } = useGps({ simulate: simPos });

  const [theme, setTheme] = useState<Theme>(() => {
    // A host that owns the display's appearance (the CarPlay scene follows
    // the car's day/night state) forces the theme by URL; the golfer's saved
    // choice only applies on screens where they CAN choose.
    /*
     * 🟥 The golfer's own choice outranks the car's.
     *
     * This read the URL FIRST, so the CarPlay window — which is launched with
     * &theme= from the car's trait collection — overrode the Night mode
     * setting entirely. The test car reports "dark" all day, so a buggy
     * dashboard in direct Queensland sun got the night map and was unreadable,
     * with a Night mode toggle on the phone that appeared to do nothing.
     *
     * The car is a good DEFAULT and a bad override: it knows about its own
     * headlights, not about a golfer squinting at a screen in the sun. So an
     * explicit saved preference wins, the car decides when there isn't one,
     * and the system setting is the last resort.
     */
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
    const forced = new URLSearchParams(location.search).get('theme');
    if (forced === 'dark' || forced === 'light') return forced;
    return matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  });
  const [dir, setDir] = useState<Directory | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [near, setNear] = useState<DirectoryCourse | null>(null);
  const [cartPaths, setCartPaths] = useState<FeatureCollection | null>(null);
  const [journey, setJourney] = useState<Journey | null>(null);
  const [destination, setDestination] = useState<Destination | null>(null);
  const [sheet, setSheet] = useState(true);
  const [prefs, setPrefs] = useState(false);
  const [places, setPlaces] = useState<Place[]>([]);
  const [micOk, setMicOk] = useState(false);
  const [listening, setListening] = useState(false);
  const dictation = useRef<Dictation | null>(null);
  const [query, setQuery] = useState('');
  const [recents, setRecents] = useState<Destination[]>(loadRecents);
  const [follow, setFollow] = useState(true);
  /*
   * The bottom panel, measured rather than guessed.
   *
   * The floating row (speed · course label · hole arrows) has to sit just above
   * whichever panel is at the bottom — the search sheet, or the trip bar during
   * a journey. Every attempt to express that as a CSS constant was wrong for
   * some state: 46vh stranded the row mid-map with the sheet shut, and
   * `56vh + 104px` assumed the sheet's body was full when an empty one is a
   * fraction of that, which threw the speedometer onto the compass.
   *
   * The panel knows its own height. A callback ref catches whichever of the two
   * is mounted, and a ResizeObserver keeps up as content, keyboard and safe
   * areas change it.
   */
  const navRef = useRef<HTMLDivElement | null>(null);
  const [bottomEl, setBottomEl] = useState<HTMLDivElement | null>(null);
  const bottomH = useRef(0);
  const [northUp, setNorthUp] = useState(false);
  const [holeNo, setHoleNo] = useState(1);
  const [confidence, setConfidence] = useState(0);
  const [muted, setMuted] = useState(!settings.voice);
  const mutedRef = useRef(!settings.voice);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const el = useRef<HTMLDivElement>(null);
  const map = useRef<MlMap | null>(null);
  const ready = useRef(false);
  const detector = useRef<DetectorState>(initialState(1));
  const loadingId = useRef<string | null>(null);
  /** ~2km cell already asked the server about, so it is asked once. */
  const askedCell = useRef<string | null>(null);
  const [remoteNearby, setRemoteNearby] = useState<DirectoryCourse[]>([]);
  const spokenStep = useRef<string | null>(null);
  const selfMove = useRef(false);
  /*
   * Latest position, for painting the blue dot outside React's render pass.
   *
   * The map's sources are rebuilt on first load and again on every theme
   * change, both inside MapLibre callbacks that cannot see React state. Without
   * this the golfer's dot would vanish on a restyle and stay gone until the
   * next fix arrived — which, standing still on a tee, can be a while.
   */
  const fixRef = useRef<LngLat | null>(null);
  /*
   * Compass state, kept in refs rather than React state.
   *
   * deviceorientation fires at up to 60Hz. Re-rendering the whole navigation
   * screen at that rate to turn a map is a waste of a battery that has to last
   * four hours, so the heading is written straight to the map and only the
   * smoothed value is carried between events.
   */
  const compassDeg = useRef<number | null>(null);
  const compassPaintedAt = useRef(0);
  const compassAsked = useRef(false);
  const resumeTimer = useRef<number | undefined>(undefined);
  const userZoom = useRef<number | null>(null);
  /** Restart the "give the map back" countdown. */
  const bumpResume = useCallback(() => {
    window.clearTimeout(resumeTimer.current);
    resumeTimer.current = window.setTimeout(() => setFollow(true), FOLLOW_RESUME_MS);
  }, []);
  useEffect(() => () => window.clearTimeout(resumeTimer.current), []);
  const paintMe = useCallback((m: MlMap) => {
    const p = fixRef.current;
    (m.getSource('me') as GeoJSONSource | undefined)?.setData(
      p ? fc([{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: p } }]) : emptyFc()
    );
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    loadDirectory().then(setDir).catch(() => {});
  }, []);

  /*
   * The mic only appears where it can actually listen.
   *
   * A dead control is worse than a missing one — it invites a tap, does
   * nothing, and gives the golfer no way to know whether the app or their voice
   * was at fault.
   */
  useEffect(() => {
    let live = true;
    void dictationAvailable().then((ok) => live && setMicOk(ok));
    return () => {
      live = false;
      void dictation.current?.stop();
    };
  }, []);

  const toggleDictation = useCallback(async () => {
    if (dictation.current) {
      await dictation.current.stop();
      dictation.current = null;
      setListening(false);
      return;
    }
    setSheet(true);
    try {
      dictation.current = await startDictation((text, final) => {
        if (text) setQuery(text);
        if (final) {
          void dictation.current?.stop();
          dictation.current = null;
          setListening(false);
        }
      });
      setListening(true);
    } catch {
      // Permission refused, or the recogniser is unavailable right now. Falling
      // back to the keyboard is the honest outcome; the field is already there.
      setListening(false);
    }
  }, []);

  // --- map ------------------------------------------------------------------
  useEffect(() => {
    if (!el.current || map.current) return;
    const m = new maplibregl.Map({
      container: el.current,
      style: navBasemapStyle(theme, settings.mapMode),
      center: [153.36, -27.86],
      zoom: 15,
      // Top-down on the car: a pitched map clamps a drag at the horizon, which
      // from the seat read as "I can't scroll any further down than this".
      pitch: CAR ? 0 : 55,
      attributionControl: { compact: true },
      dragRotate: false,
    });
    map.current = m;
    // Demo/screenshot mode exposes the map so a capture run can frame it
    // (zoom out to the wide CarPlay dashboard look). No-op in normal use.
    if (DEMO || SIM) (window as unknown as { __gcMap?: unknown }).__gcMap = m;
    const ro = new ResizeObserver(() => m.resize());
    ro.observe(el.current);

    /*
     * A one-finger PAN is the golfer choosing to look somewhere else, so
     * following yields. A ZOOM is not: "show me more of the hole" still means
     * "and keep me on screen", which is how every car navigation app behaves.
     *
     * Zoom used to cancel follow as well — and on a touchscreen a pinch fires
     * dragstart too, so a single zoom-out froze the map and left the golfer
     * with only a small Recentre button as the way back. Reported from the
     * course: "it moved when I opened the app and zoomed out, but didn't
     * update as I moved."
     */
    const isPinch = (ev: unknown) => ((ev as TouchEvent | undefined)?.touches?.length ?? 0) > 1;
    const yieldToUser = (e: { originalEvent?: unknown }) => {
      if (!e?.originalEvent || selfMove.current) return;
      if (isPinch(e.originalEvent)) return;
      setFollow(false);
      bumpResume();
    };
    m.on('dragstart', yieldToUser);
    // Touching the map means "I want to see the map".
    m.on('click', () => {
      setSheet(false);
      (document.activeElement as HTMLElement | null)?.blur();
    });

    m.on('load', () => {
      m.addSource('route', { type: 'geojson', data: emptyFc() });
      m.addLayer({
        id: 'route-casing',
        type: 'line',
        source: 'route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#0b3f8f', 'line-width': 13, 'line-opacity': 0.9 },
      });
      m.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#2f7fff', 'line-width': 8 },
      });
      m.addSource('hole', { type: 'geojson', data: emptyFc() });
      m.addLayer({
        id: 'hole-line',
        type: 'line',
        source: 'hole',
        paint: { 'line-color': '#38e07b', 'line-width': 3, 'line-dasharray': [2, 2], 'line-opacity': 0.85 },
      });
      m.addSource('dest', { type: 'geojson', data: emptyFc() });
      m.addLayer({
        id: 'dest-dot',
        type: 'circle',
        source: 'dest',
        paint: { 'circle-radius': 9, 'circle-color': '#ffc247', 'circle-stroke-color': '#1b1b1b', 'circle-stroke-width': 2 },
      });
      // The golfer. Added last so it draws over the route and the hole line —
      // whatever else is on the map, you can always find yourself.
      m.addSource('me', { type: 'geojson', data: emptyFc() });
      m.addLayer({
        id: 'me-dot',
        type: 'circle',
        source: 'me',
        paint: { 'circle-radius': 8, 'circle-color': ME_BLUE, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 3 },
      });
      paintMe(m);
      ready.current = true;
    });

    return () => {
      ro.disconnect();
      m.remove();
      map.current = null;
      ready.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restyling swaps the basemap; sources and layers have to be rebuilt, which
  // MapLibre handles via the style-load event.
  useEffect(() => {
    const m = map.current;
    if (!m || !ready.current) return;
    ready.current = false;
    m.setStyle(navBasemapStyle(theme, settings.mapMode));
    m.once('styledata', () => {
      /*
       * 🟥 Re-arm `ready` on EVERY style load, not just the ones that rebuild
       * sources.
       *
       * This used to `return` early when the sources survived — and MapLibre
       * keeps them whenever it can diff the old style into the new one, which
       * is exactly what happens toggling dark<->light within the same map mode.
       * The early return skipped `ready.current = true`, so the flag stayed
       * false forever and the guard at the top of this effect silently killed
       * every later switch. One toggle poisoned it; after that neither theme
       * nor Road/Satellite/Hybrid did anything, with no error to show for it.
       * Reported twice from the car before it was found.
       */
      if (!m.getSource('route')) {
        // Dim everything that is not the hole being played. Two bands, same
        // opacities as HoleMap (0.45 near, 0.85 stacked far), added FIRST so
        // the route, the hole line and the dot stay bright on top.
        m.addSource('mask', { type: 'geojson', data: emptyFc() });
        m.addLayer({ id: 'mask-far', type: 'fill', source: 'mask', filter: ['==', ['get', 'band'], 'far'], paint: { 'fill-color': '#04120a', 'fill-opacity': 0.73 } });
        m.addLayer({ id: 'mask-near', type: 'fill', source: 'mask', filter: ['==', ['get', 'band'], 'near'], paint: { 'fill-color': '#04120a', 'fill-opacity': 0.45 } });
        m.addSource('route', { type: 'geojson', data: emptyFc() });
        m.addLayer({ id: 'route-casing', type: 'line', source: 'route', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#0b3f8f', 'line-width': 13, 'line-opacity': 0.9 } });
        m.addLayer({ id: 'route-line', type: 'line', source: 'route', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#2f7fff', 'line-width': 8 } });
        m.addSource('hole', { type: 'geojson', data: emptyFc() });
        m.addLayer({ id: 'hole-line', type: 'line', source: 'hole', paint: { 'line-color': '#38e07b', 'line-width': 3, 'line-dasharray': [2, 2], 'line-opacity': 0.85 } });
        m.addSource('dest', { type: 'geojson', data: emptyFc() });
        m.addLayer({ id: 'dest-dot', type: 'circle', source: 'dest', paint: { 'circle-radius': 9, 'circle-color': '#ffc247', 'circle-stroke-color': '#1b1b1b', 'circle-stroke-width': 2 } });
        m.addSource('me', { type: 'geojson', data: emptyFc() });
        m.addLayer({ id: 'me-dot', type: 'circle', source: 'me', paint: { 'circle-radius': 8, 'circle-color': ME_BLUE, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 3 } });
      }
      paintMe(m);
      ready.current = true;
    });
  }, [theme, settings.mapMode]);

  // --- course detection -----------------------------------------------------
  useEffect(() => {
    if (!dir || !fix) return;
    let best: DirectoryCourse | null = null;
    let bestD = Infinity;
    for (const c of dir.courses) {
      const d = distanceM(fix.pos, [c.lng, c.lat]);
      if (d < bestD) { best = c; bestD = d; }
    }
    if (!best || bestD > ON_COURSE_M) {
      setNear(null);
      setCourse(null);
      loadingId.current = null;
      /*
       * Outside the bundled directory, ask the server.
       *
       * The directory is Australian, and everything downstream of it — hole
       * detection, the tee and green shortcuts, the dashboard tile — needs a
       * course. A golfer anywhere else got a road map and silence. Keyed to a
       * ~2km cell so driving around does not re-ask on every fix, and only
       * ever from here, where the local answer has already come back empty.
       */
      const cell = `${(fix.pos[1] * 50).toFixed(0)}_${(fix.pos[0] * 50).toFixed(0)}`;
      if (askedCell.current !== cell) {
        askedCell.current = cell;
        void nearbyCourses(fix.pos).then((found) => {
          setRemoteNearby(found);
          const c = found[0];
          if (!c || distanceM(fix.pos, [c.lng, c.lat]) > ON_COURSE_M) return;
          setNear(c);
          if (loadingId.current === c.id) return;
          loadingId.current = c.id;
          detector.current = initialState(1);
          update({ courseId: c.id, courseName: c.name });
          void loadCourse(c.id).then(({ course: full }) => {
            if (loadingId.current === c.id && full.holes.length) setCourse(full);
          });
        });
      }
      return;
    }
    setNear(best);
    if (loadingId.current === best.id) return;
    loadingId.current = best.id;
    detector.current = initialState(1);
    update({ courseId: best.id, courseName: best.name });
    void loadCourse(best.id).then(({ course: c }) => {
      if (loadingId.current === best!.id && c.holes.length) setCourse(c);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dir, fix?.pos[0], fix?.pos[1]]);

  useEffect(() => {
    // Cart paths (and therefore turn-by-turn) are keyed to the course you're
    // standing on, NOT to whether its holes are mapped. The Palms has a full
    // cart-path network but no per-hole tee/green geometry, so gating this on
    // `course` — which only sets once holes clear the geometry guard — left
    // navigation dead there. Drive it off `near`, the on-course directory
    // match, so paths (and the directions banner) work regardless of mapping.
    const id = near?.id;
    if (!id) return setCartPaths(null);
    const centre: LngLat =
      course?.id === id && course.centre ? course.centre : [near.lng, near.lat];
    loadCartPaths(id, centre).then(setCartPaths).catch(() => {});
  }, [near?.id, near?.lng, near?.lat, course?.id, course?.centre]);

  const graph = useMemo(() => buildGraph(cartPaths), [cartPaths]);

  // --- hole -----------------------------------------------------------------
  const hole: Hole | null = useMemo(() => {
    if (!course?.holes.length) return null;
    return course.holes.find((h) => h.number === holeNo) ?? course.holes[0];
  }, [course, holeNo]);

  useEffect(() => {
    if (!course || !fix || fix.stale) return;
    detector.current = detectStep(detector.current, {
      course,
      pos: fix.pos,
      accuracy: fix.accuracy,
      teeColour: settings.teeColour,
      now: fix.ts,
    });
    setHoleNo(detector.current.hole);
    setConfidence(detector.current.confidence);
  }, [fix, course, settings.teeColour]);

  // --- publish to the dashboard tile ---------------------------------------
  useEffect(() => {
    if (!fix) return;
    const tee = hole ? hole.tees[settings.teeColour] ?? hole.tees.White : null;
    void pushYardage({
      course: course?.name ?? near?.name ?? '',
      hole: hole?.number ?? 0,
      par: hole?.par ?? 0,
      metres: hole ? distanceM(fix.pos, hole.green.middle) : 0,
      unit: settings.unit,
      playerLat: fix.pos[1],
      playerLng: fix.pos[0],
      speed: fix.speed,
      heading: fix.heading,
      ...(tee && hole
        ? {
            teeLat: tee[1], teeLng: tee[0],
            greenLat: hole.green.middle[1], greenLng: hole.green.middle[0],
          }
        : {}),
    });
  }, [fix, hole, course, near, settings.unit, settings.teeColour]);

  /*
   * Live state the compass listener needs, without re-subscribing.
   *
   * The listener is attached once and lives for the session; closing over
   * follow/northUp/speed directly would freeze them at their values on the day
   * it was created.
   */
  const live = useRef({ follow: true, northUp: false, speed: 0, on: true });
  useEffect(() => {
    live.current = { follow, northUp, speed: fix?.speed ?? 0, on: settings.compass };
  }, [follow, northUp, fix, settings.compass]);

  /*
   * Keep the sheet above the keyboard.
   *
   * Tapping the search box opened the keyboard, the sheet stayed anchored to
   * the bottom of a layout viewport that now extended behind it, and the map
   * disappeared under a panel whose own search field had been pushed off the
   * top of the screen — the field you were typing into.
   *
   * visualViewport is the only thing that reports where the keyboard actually
   * is. When Capacitor resizes the web view natively the inset comes out as
   * zero and nothing changes, so this is correct either way rather than a
   * second guess layered on the first.
   */
  useEffect(() => {
    const vv = window.visualViewport;
    const nav = navRef.current;
    if (!vv || !nav) return;
    const apply = () => {
      const inset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      nav.style.setProperty('--n-kb', `${inset}px`);
      /*
       * The VISIBLE height, which is not what vh units measure.
       *
       * vh is the layout viewport and keeps counting the strip the keyboard is
       * sitting on, so a body capped at 56vh grew taller than the screen with
       * the keyboard up and shoved the search field off the top — leaving the
       * golfer scrolling results instead of typing. This is the only number
       * that shrinks when the keyboard appears.
       */
      nav.style.setProperty('--n-vh', `${Math.round(vv.height)}px`);
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
    };
  }, []);

  const compassStop = useRef<(() => void) | null>(null);
  const startCompass = useCallback(async () => {
    if (compassStop.current || !compassSupported()) return;
    compassAsked.current = true;
    if (!(await requestCompass())) return;

    compassStop.current = watchCompass((deg) => {
      compassDeg.current = smoothHeading(compassDeg.current, deg);
      const m = map.current;
      const s = live.current;
      // North-up is an explicit request for a fixed map. Panning away from
      // follow is the golfer taking control. Above walking pace GPS course is
      // the better signal — it tracks where the buggy is GOING, where the
      // compass tracks where the phone happens to be pointed on a seat.
      if (!m || !s.on || !s.follow || s.northUp || s.speed > 1) return;

      // ~15fps is smooth to the eye and a quarter of the work of 60.
      const now = Date.now();
      if (now - compassPaintedAt.current < 66) return;
      const drift = Math.abs(((compassDeg.current - m.getBearing() + 540) % 360) - 180);
      if (drift < 1.5) return; // magnetometer noise, not a turn

      compassPaintedAt.current = now;
      selfMove.current = true;
      m.setBearing(compassDeg.current);
      window.setTimeout(() => (selfMove.current = false), 0);
    });
  }, []);

  /*
   * iOS will only grant motion access from inside a user gesture, so the first
   * touch anywhere in the app is what asks. Requesting on mount fails silently,
   * which would look exactly like a compass that does not work.
   */
  useEffect(() => {
    const ask = () => {
      if (settings.compass && !compassAsked.current) void startCompass();
    };
    window.addEventListener('pointerdown', ask, { once: true });
    return () => window.removeEventListener('pointerdown', ask);
  }, [settings.compass, startCompass]);

  useEffect(() => () => compassStop.current?.(), []);

  /*
   * 🟥 THE CAR SCREEN LOADS THIS PAGE (/?carplay=1), and until 2026-09-06 this
   * page registered no CarPlay handlers at all. Every native map button —
   * hole chevrons, zoom, pan mode, and the finger drag the delegate forwards —
   * called window.__gocaddyCommand on a page that had never defined it, and
   * Swift logged "not handled" into a car with no console. From the seat:
   * "screen still don't move left right up down, no swipe or anything."
   *
   * A zoom the golfer chose has to survive the follow camera, which otherwise
   * re-applies its speed-based zoom on the next fix, one second later — the
   * zoom buttons looked dead for the same reason the chevrons did.
   */
  const pickHole = useCallback((n: number) => {
    if (!course?.holes.length) return;
    const nums = course.holes.map((h) => h.number);
    const target = Math.max(Math.min(...nums), Math.min(Math.max(...nums), n));
    // Manual wins: the detector's own hold, or the next fix snaps it back.
    detector.current = {
      ...detector.current, hole: target, confidence: 1,
      manualUntil: Date.now() + 180_000, candidate: null, candidateSince: null,
    };
    setHoleNo(target);
    setConfidence(1);
    /*
     * The arrow keys SHOW the hole: tee at the bottom, green at the top, the
     * whole thing on screen, and the blue dot too when it is on this hole.
     * Following stops so the frame holds; recentre brings the camera back.
     */
    const h = course.holes.find((x) => x.number === target);
    const m = map.current;
    if (!h || !m) return;
    const tee = h.tees[settings.teeColour] ?? h.tees.White;
    if (!tee) return;
    const pts: LngLat[] = [tee, h.green.front, h.green.middle, h.green.back, ...(h.centreline ?? [])];
    const me = fixRef.current;
    if (me && Math.min(distanceM(me, tee), distanceM(me, h.green.middle)) < 400) pts.push(me);
    const b = pts.reduce((acc, p) => acc.extend(p as [number, number]), new maplibregl.LngLatBounds(pts[0] as [number, number], pts[0] as [number, number]));
    window.clearTimeout(resumeTimer.current);
    setFollow(false);
    userZoom.current = null;
    selfMove.current = true;
    m.fitBounds(b, { bearing: bearingDeg(tee, h.green.middle), pitch: 0, padding: { top: 110, bottom: 70, left: 70, right: 70 }, duration: 600 });
    setTimeout(() => (selfMove.current = false), 700);
  }, [course, settings.teeColour]);
  useEffect(() => registerCarPlayCommands({
    holeNext: () => pickHole(holeNo + 1),
    holePrev: () => pickHole(holeNo - 1),
    zoomIn: () => { const m = map.current; if (!m) return; userZoom.current = m.getZoom() + 1; m.zoomTo(userZoom.current, { duration: 220 }); },
    zoomOut: () => { const m = map.current; if (!m) return; userZoom.current = m.getZoom() - 1; m.zoomTo(userZoom.current, { duration: 220 }); },
    zoomCycle: () => {
      const m = map.current; if (!m) return;
      const levels = [15.4, 16.6, 17.8];
      const z = m.getZoom();
      userZoom.current = levels.find((l) => l > z + 0.15) ?? levels[0];
      m.zoomTo(userZoom.current, { duration: 260 });
    },
    // The blue dot keeps tracking underneath; only the camera stops chasing it.
    // No countdown on the car ("remove the autolock that locks you in"): a
    // panned map stays panned until the native recentre button is pressed.
    pan: (dx, dy) => {
      const m = map.current; if (!m) return;
      window.clearTimeout(resumeTimer.current); setFollow(false);
      const c = m.getContainer();
      m.panBy([dx * c.clientWidth * 0.45, dy * c.clientHeight * 0.45], { duration: 260 });
    },
    panPx: (dx, dy) => {
      const m = map.current; if (!m) return;
      window.clearTimeout(resumeTimer.current); setFollow(false);
      m.panBy([-dx, -dy], { duration: 0 });
    },
    panBegin: () => { window.clearTimeout(resumeTimer.current); setFollow(false); },
    recentre: () => { userZoom.current = null; setFollow(true); },
    muteToggle: () => {
      const next = !mutedRef.current;
      mutedRef.current = next;
      if (next) stopSpeaking(); else void unlockVoice();
      setMuted(next);
      update({ voice: !next });
    },
  }), [holeNo, pickHole, update]);

  /*
   * TEMPORARY car-screen diagnostic, 2026-09-06 (self-expires 18:00 AEST):
   * follow · pitch · zoom · last CarPlay command, in the hole pill. The seat
   * reports "can't pan any further" and there is no console in a buggy.
   */
  const [diag, setDiag] = useState('');
  useEffect(() => {
    if (!CAR || Date.now() > Date.UTC(2026, 8, 6, 8, 0)) return;
    const id = window.setInterval(() => {
      const m = map.current; if (!m) return;
      const last = (window as unknown as { __gcLastCmd?: string }).__gcLastCmd ?? '-';
      setDiag(`${follow ? 'follow' : 'free'} · p${m.getPitch().toFixed(0)} · z${m.getZoom().toFixed(1)} · ${last}`);
    }, 500);
    return () => window.clearInterval(id);
  }, [follow]);

  // Tell the car which button set to show. No-op anywhere but inside the
  // CarPlay web view, where Swift registered the "gocaddy" handler.
  useEffect(() => {
    try {
      (window as unknown as { webkit?: { messageHandlers?: { gocaddy?: { postMessage: (m: unknown) => void } } } })
        .webkit?.messageHandlers?.gocaddy?.postMessage({ follow });
    } catch { /* not in the car */ }
  }, [follow]);

  // --- camera ---------------------------------------------------------------
  useEffect(() => {
    const m = map.current;
    if (!m || !fix || !follow) return;
    selfMove.current = true;
    const moving = fix.speed > 1;
    m.easeTo({
      center: fix.pos,
      // Zoom out as speed rises, the way every navigation app does: parked you
      // want the driveway, at road speed you want the next turn.
      zoom: userZoom.current ?? (moving ? Math.max(15.2, 17.6 - fix.speed * 0.22) : 17.4),
      // Stopped, the compass is the only thing that knows which way the golfer
      // is facing; GPS course is meaningless at 0 km/h and left the map frozen
      // whichever way they turned.
      bearing: northUp ? 0 : moving ? fix.heading : compassDeg.current ?? m.getBearing(),
      pitch: northUp || CAR ? 0 : 55,
      /*
       * Centre on the visible map, not on the canvas.
       *
       * The canvas is the whole screen and the sheet covers its lower half, so
       * centring the golfer put them underneath the sheet — invisible, however
       * the dot is drawn. Padding shifts the centre up into the strip that can
       * actually be seen, which is why every maps app does this and why the
       * dot appeared to be missing rather than hidden.
       */
      padding: { top: 0, right: 0, bottom: bottomH.current, left: 0 },
      duration: 900,
    });
    setTimeout(() => (selfMove.current = false), 1000);
  }, [fix, follow, northUp]);

  /**
   * Open a course's golf map. Remembering the choice first means /map has the
   * course before it mounts, so it opens on holes rather than on a picker.
   */
  const openCourse = useCallback((courseId: string, courseName: string) => {
    try {
      const KEY = 'fairway.settings.v1';
      const cur = JSON.parse(localStorage.getItem(KEY) ?? '{}');
      localStorage.setItem(KEY, JSON.stringify({ ...cur, courseId, courseName }));
    } catch { /* fresh settings */ }
    location.href = '/map';
  }, []);

  // --- route ----------------------------------------------------------------
  const startJourney = useCallback(
    async (dest: Destination, mode: TravelMode) => {
      if (!fix) return;
      setDestination(dest);
      setSheet(false);
      setFollow(true);
      const j = await planJourney(graph, fix.pos, dest.pos, {
        mode,
        unit: settings.unit,
        destination: dest.name,
      });
      setJourney(j);
      spokenStep.current = null;
      setRecents((r) => {
        const next = [dest, ...r.filter((x) => x.name !== dest.name)].slice(0, 6);
        localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
        return next;
      });
    },
    [fix, graph, settings.unit]
  );

  // Sim mode: once the course and its cart-path graph have loaded, auto-route to
  // the farthest point on the network — a long, turn-rich trip that exercises the
  // real maneuver engine — then step the simulated position along the routed line
  // so the banner counts down and the maneuvers advance, hands-free.
  useEffect(() => {
    if (!SIM || journey || !fix || !cartPaths || graph.nodes.length < 4) return;
    let far = fix.pos;
    let best = 0;
    for (const f of cartPaths.features) {
      const line = f.geometry?.type === 'LineString' ? (f.geometry.coordinates as LngLat[]) : [];
      for (const p of line) {
        const d = distanceM(fix.pos, p);
        if (d > best) { best = d; far = p; }
      }
    }
    void startJourney({ name: 'Far side of the course', pos: far, kind: 'place' }, 'drive');
  }, [SIM, journey, fix, cartPaths, graph, startJourney]);

  useEffect(() => {
    if (!SIM || !journey?.line?.length) return;
    let i = 0;
    const id = window.setInterval(() => {
      i = Math.min(i + 1, journey.line.length - 1);
      setSimPos(journey.line[i]);
      if (i >= journey.line.length - 1) window.clearInterval(id);
    }, 1400);
    return () => window.clearInterval(id);
  }, [SIM, journey]);

  // ?demo: mount the scripted route and expose a jump so a screenshot run can
  // land on a named maneuver (window.__gcDemoAt('exit'|'nowhere'|'motorway')).
  useEffect(() => {
    if (!DEMO) return;
    setDestination(DEMO_DESTINATION);
    setJourney(DEMO_JOURNEY);
    setSheet(false);
    setFollow(true);
    const w = window as unknown as { __gcDemoAt?: (n: string) => void };
    w.__gcDemoAt = (n: string) => {
      const p = DEMO_CAPTURES[n];
      if (p) { setSimPos(p); setFollow(true); }
    };
    return () => { delete w.__gcDemoAt; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DEMO]);

  const endJourney = () => {
    setJourney(null);
    setDestination(null);
    setSheet(true);
    stopSpeaking();
  };

  /*
   * The blue dot, from the moment there is a fix.
   *
   * It used to be that the only marker on the map was the amber destination,
   * which by definition cannot exist until a course has been picked — so
   * opening the app showed a map with nothing on it and no sign the GPS was
   * even alive. Your own position does not depend on choosing anything.
   *
   * Kept in its own effect because a fix lands every few seconds and the route
   * and hole geometry have no reason to be recomputed that often.
   */
  useEffect(() => {
    fixRef.current = fix?.pos ?? null;
    const m = map.current;
    if (!m || !ready.current) return;
    paintMe(m);
  }, [fix, paintMe]);

  // Paint route, hole line and destination.
  useEffect(() => {
    const m = map.current;
    if (!m || !ready.current) return;
    const set = (id: string, data: FeatureCollection) =>
      (m.getSource(id) as GeoJSONSource | undefined)?.setData(data);

    set('route', journey?.line.length
      ? fc([{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: journey.line } }])
      : emptyFc());

    set('dest', destination
      ? fc([{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: destination.pos } }])
      : emptyFc());

    const tee = hole ? hole.tees[settings.teeColour] ?? hole.tees.White : null;
    set('hole', hole && tee && !journey
      ? fc([{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [tee, hole.green.middle] } }])
      : emptyFc());
    // The blackout: off during a street journey, and off if the golfer turned
    // "Dim other holes" off on the phone.
    const spine = hole && !journey && settings.indicators.focus !== false ? holeSpine(hole, settings.teeColour) : null;
    set('mask', spine
      ? fc([
          corridorMask(spine, CORRIDOR_WIDTH_M * 2.6, { band: 'far' }) as Feature,
          corridorMask(spine, CORRIDOR_WIDTH_M * 1.5, { band: 'near' }) as Feature,
        ])
      : emptyFc());
  }, [journey, destination, hole, settings.teeColour, settings.indicators.focus, theme]);

  // --- voice ----------------------------------------------------------------
  const next = useMemo(
    () => (journey && fix ? upcoming(journey.steps, fix.pos, journey.line) : null),
    [journey, fix]
  );

  useEffect(() => {
    if (muted || !next || !voiceSupported()) return;
    // Speak an instruction once, at the point it becomes useful — not on every
    // fix, which would repeat it every few seconds all the way to the turn.
    const id = `${next.step.at.join(',')}:${next.step.maneuver}`;
    if (spokenStep.current === id || next.distanceM > 120) return;
    spokenStep.current = id;
    speak({ kind: 'warning', text: next.step.text });
  }, [next, muted]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav || !bottomEl) return;
    const apply = () => {
      const h = Math.round(bottomEl.getBoundingClientRect().height);
      nav.style.setProperty('--n-row', `${h + 16}px`);
      // The camera needs the same figure, to keep the golfer out from behind
      // the sheet. See the camera effect.
      bottomH.current = h;
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(bottomEl);
    return () => ro.disconnect();
  }, [bottomEl]);

  /*
   * World search, alongside the course directory.
   *
   * Debounced and cancelling — see lib/places. Kept in its own effect so a
   * network round trip can never hold up the local list, which is instant and
   * has to stay that way on a course with no signal.
   */
  const placeSearch = useMemo(() => createPlaceSearch(setPlaces), []);
  useEffect(() => () => placeSearch.stop(), [placeSearch]);
  useEffect(() => {
    placeSearch.search(query, fix?.pos ?? null);
    // Position is deliberately not a dependency: re-running the search on every
    // GPS fix would fire a request every few seconds while the box sits open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, placeSearch]);

  // --- search ---------------------------------------------------------------
  const hits = useMemo(() => {
    if (!dir) return [];
    const near2 = fix ? fix.pos : null;
    // One character is enough. Waiting for two means the first keystroke looks
    // like nothing happened, which is the moment a search feels broken.
    return query.trim().length >= 1
      ? searchDirectory(dir, { query, near: near2, sort: 'name', limit: 100 })
      : near2
        ? searchDirectory(dir, { near: near2, sort: 'distance', limit: 5 }).filter(
            (c) => (c.distanceM ?? Infinity) <= NEAREST_MAX_M
          )
        : [];
  }, [dir, query, fix]);

  /*
   * What to show under NEAREST.
   *
   * The bundled directory when it has something local, the server's worldwide
   * answer when it does not. A golfer in Arizona sees Arizona courses, not the
   * closest thing in Australia.
   */
  const nearestList = useMemo(
    () => (hits.length || query ? hits : remoteNearby.slice(0, 5)),
    [hits, query, remoteNearby]
  );

  /*
   * Everything searchable in one list.
   *
   * The tees, greens and clubhouse of the course being played were reachable
   * only as chips, and only while the box was empty — so typing "green" found
   * 1,449 courses and not the green fifty metres away. They are ordinary rows
   * now, matched and sorted with the rest.
   */
  const placeHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !course?.holes.length) return [];
    const out: Destination[] = [{ name: 'Clubhouse', pos: course.centre, kind: 'place' }];
    for (const h of course.holes) {
      const tee = h.tees[settings.teeColour] ?? h.tees.White;
      if (tee) out.push({ name: `Tee ${h.number}`, pos: tee, kind: 'hole' });
      out.push({ name: `Green ${h.number}`, pos: h.green.middle, kind: 'hole' });
    }
    return out
      .filter((d) => d.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [query, course, settings.teeColour]);

  /*
   * The same place must not appear twice.
   *
   * The world geocoder holds golf courses too, so searching an exact course
   * name returned it once from our directory and again from OpenStreetMap,
   * usually under a slightly different name — "Palms Course" and "The Palms
   * Golf Course". Containment either way catches that, where an equality check
   * does not.
   */
  const worldHits = useMemo(() => {
    const norm = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    return places.filter((p) => {
      const b = norm(p.name);
      return !hits.some((h) => {
        const a = norm(h.name);
        if (a === b || a.includes(b) || b.includes(a)) return true;
        // Names drift between datasets — "Palms Course" against "The Palms Golf
        // Course" — so position is the backstop. Anything this close to a
        // course we are already listing IS that course.
        return distanceM(p.pos, [h.lng, h.lat]) < 400;
      });
    });
  }, [places, hits]);

  const shortcuts = useMemo(() => {
    const out: Destination[] = [];
    if (course?.holes.length) {
      const nextHole = course.holes.find((h) => h.number === holeNo + 1) ?? course.holes[0];
      const tee = nextHole.tees[settings.teeColour] ?? nextHole.tees.White;
      out.push({ name: `Tee ${nextHole.number}`, pos: tee, kind: 'hole' });
      if (hole) out.push({ name: `Green ${hole.number}`, pos: hole.green.middle, kind: 'hole' });
      out.push({ name: 'Clubhouse', pos: course.centre, kind: 'place' });
    }
    return out;
  }, [course, hole, holeNo, settings.teeColour]);

  /*
   * Tee options come from the course being played, not a fixed list: courses
   * carry whatever sets they were mapped with, and offering a colour this
   * layout has no tees for would silently fall back to White mid-round.
   */
  const teeOptions = useMemo(() => {
    const fromCourse = course?.holes?.[0]?.tees ? Object.keys(course.holes[0].tees) : [];
    return fromCourse.length ? fromCourse : ['White', 'Yellow', 'Blue', 'Red'];
  }, [course]);

  const speedKmh = Math.round((fix?.speed ?? 0) * 3.6);
  // 'Finding you' is GPS state, not course state: once a fix exists, an
  // off-course drive is just road navigation and the pill has nothing to
  // say — showing 'Finding you' over a live position dot reads as broken.
  const label = journey
    ? destination?.name
    : hole
      ? `Hole ${hole.number} · par ${hole.par}`
      : (near?.name ?? (fix ? null : 'Finding you'));

  return (
    <div className={`nav${journey ? ' nav--routing' : ''}`} ref={navRef}>
      <div ref={el} className="nav-map" />

      {/* The header slot. While navigating this is the turn instruction; the
          rest of the time it is the hole, which is the one fact a golfer
          glancing down wants and which does not belong floating over the
          middle of the map. Only ever one of the two. */}
      {!journey && label && (
        <div className={`nav-tophole ${confidence >= 0.35 ? '' : 'dim'}`}>{label}{diag ? <small style={{ display: 'block', fontSize: 12, opacity: 0.8 }}>{diag}</small> : null}</div>
      )}

      {journey && next && (
        <div className="nav-banner" role="status" aria-live="polite">
          <div className="nav-banner-arrow"><ManeuverIcon maneuver={next.step.maneuver} /></div>
          <div className="nav-banner-text">
            <span className="nav-banner-eyebrow">
              Directions{destination ? ` · ${destination.name}` : ''}
            </span>
            <b>{next.step.text.replace(/^In [^,]+, /, '')}</b>
            <span className="nav-banner-dist">
              {formatDistance(next.distanceM, settings.unit)}{settings.unit}
            </span>
          </div>
        </div>
      )}

      <button className="nav-btn nav-menu" onClick={() => setPrefs(true)} aria-label="Settings">
        <svg viewBox="0 0 24 24" width="23" height="23" aria-hidden="true">
          <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="2" />
          <path
            d="M19.4 13.5a7.7 7.7 0 0 0 0-3l1.9-1.4-1.9-3.3-2.2.9a7.7 7.7 0 0 0-2.6-1.5L14.3 2h-3.8l-.3 2.3a7.7 7.7 0 0 0-2.6 1.5l-2.2-.9L3.5 8.2l1.9 1.4a7.7 7.7 0 0 0 0 3l-1.9 1.4 1.9 3.3 2.2-.9a7.7 7.7 0 0 0 2.6 1.5l.3 2.3h3.8l.3-2.3a7.7 7.7 0 0 0 2.6-1.5l2.2.9 1.9-3.3Z"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"
          />
        </svg>
      </button>

      <button
        className={`nav-btn nav-compass ${northUp ? 'on' : ''}`}
        onClick={() => setNorthUp((v) => !v)}
        aria-label="Toggle orientation"
      >
        <svg viewBox="0 0 40 40" width="26" height="26">
          <polygon points="20,6 26,24 20,20 14,24" fill="#ff4d4d" />
          <polygon points="20,34 14,18 20,22 26,18" fill="currentColor" opacity=".45" />
        </svg>
      </button>

      <button
        className={`nav-btn nav-mute ${muted ? 'off' : ''}`}
        onClick={() => {
          if (muted) unlockVoice();
          else stopSpeaking();
          setMuted((v) => !v);
          update({ voice: muted });
        }}
        aria-label="Toggle voice"
      >
        <svg viewBox="0 0 24 24" width="23" height="23" aria-hidden="true">
          <path d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4Z" fill="currentColor" />
          {muted ? (
            <path d="M16 9.5l5 5M21 9.5l-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
          ) : (
            <path d="M15.8 9a4.4 4.4 0 0 1 0 6M18.4 6.6a8 8 0 0 1 0 10.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
          )}
        </svg>
      </button>

      <div className="nav-speed">
        <b>{speedKmh}</b>
        <span>km/h</span>
      </div>

      <div className="nav-centrestack">
        {!follow && (
          <button className="nav-recentre" onClick={() => setFollow(true)}>Recentre</button>
        )}
      </div>


      {journey ? (
        <div className="nav-trip" ref={setBottomEl}>
          <div className="nav-trip-figures">
            <b>{formatTotalDistance(journey.distanceM, settings.unit)}</b>
            <span>{Math.max(1, Math.round(journey.durationS / 60))} min</span>
          </div>
          <button className="nav-end" onClick={endJourney}>End</button>
        </div>
      ) : (
        <div className={`nav-sheet ${sheet ? 'open' : ''}`} ref={setBottomEl}>
          <button className="nav-grab" onClick={() => setSheet((v) => !v)} aria-label="Toggle panel" />

          <div className="nav-searchrow">
            <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
              <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
              <path d="M15.4 15.4 21 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
            <input
              value={query}
              placeholder="Where to?"
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setSheet(true)}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            />
            {micOk && (
              <button
                className={`nav-mic ${listening ? 'live' : ''}`}
                onClick={() => void toggleDictation()}
                aria-label={listening ? 'Stop listening' : 'Search by voice'}
              >
                <svg viewBox="0 0 24 24" width="21" height="21" aria-hidden="true">
                  <rect x="9" y="2.5" width="6" height="11" rx="3" fill="currentColor" />
                  <path
                    d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M8.5 21h7"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Everything below the search row collapses; the row itself never
              does, so the sheet's shut height is whatever the row needs. */}
          <div className="nav-sheet-body">

          {!!shortcuts.length && !query && (
            <div className="nav-chips">
              {shortcuts.map((s) => (
                <button key={s.name} onClick={() => void startJourney(s, 'drive')}>
                  <b>{s.name}</b>
                </button>
              ))}
            </div>
          )}

          {!!placeHits.length && (
            <>
              <div className="nav-heading">ON THIS COURSE</div>
              {placeHits.map((d) => (
                <button key={d.name} className="nav-row" onClick={() => void startJourney(d, 'drive')}>
                  <b>{d.name}</b>
                  <span>{course?.name}</span>
                </button>
              ))}
            </>
          )}

          {!!nearestList.length && (
            <>
              <div className="nav-heading">{query ? 'COURSES' : 'NEAREST'}</div>
              {nearestList.map((c) => (
                <div key={c.id} className="nav-row nav-row-split">
                  {/* Tapping the club's name opens the course — holes, yardages,
                      bunker carries. Someone who types their own club name is
                      telling us which course they play, not asking to be driven
                      somewhere; and if they are already standing on it, routing
                      them to it is nonsense. Directions stay one tap away. */}
                  <button
                    className="nav-row-main"
                    onClick={() => openCourse(c.id, c.name)}
                  >
                    <b>{c.name}</b>
                    <span>{[c.suburb, STATE_NAMES[c.state] ?? c.state].filter(Boolean).join(' · ')}</span>
                  </button>
                  <button
                    className="nav-drive-btn"
                    onClick={() => void startJourney({ name: c.name, pos: [c.lng, c.lat], kind: 'course' }, 'drive')}
                  >
                    Drive there
                  </button>
                </div>
              ))}
            </>
          )}

          {!!worldHits.length && (
            <>
              <div className="nav-heading">PLACES</div>
              {worldHits.map((p) => (
                <button
                  key={`${p.name}-${p.pos[0]}-${p.pos[1]}`}
                  className="nav-row"
                  onClick={() => void startJourney({ name: p.name, pos: p.pos, kind: 'place' }, 'drive')}
                >
                  <b>{p.name}</b>
                  <span>{p.where}</span>
                </button>
              ))}
            </>
          )}

          {!query && !!recents.length && (
            <>
              <div className="nav-heading">RECENT</div>
              {recents.map((r) => (
                <button key={r.name} className="nav-row" onClick={() => void startJourney(r, 'drive')}>
                  <b>{r.name}</b>
                  <span>{r.kind === 'course' ? 'Course' : r.kind === 'hole' ? 'On this course' : 'Place'}</span>
                </button>
              ))}
            </>
          )}

          <div className="nav-foot">
            {BRAND.name} · {dir ? `${dir.count.toLocaleString()} courses` : 'loading'}
            {fix ? ` · GPS ±${Math.round(fix.accuracy)}m` : ' · waiting for GPS'}
            {/*
              The tile's health, on the one screen that is always open.

              This screen replaced the blank AutoApp as the app's root but did
              not carry over its diagnostic line, so a publish that failed every
              single time had nowhere to say so — which is how an unregistered
              plugin survived long enough to look like a data problem. A count
              that climbs is proof the dashboard tile is being fed.
            */}
            {widgetError() ? ` · TILE ERR ${widgetError()}` : ` · tile ${widgetPushCount()}`}
          </div>
          </div>
        </div>
      )}

      {prefs && (
        <div className="nav-prefs">
          <div className="nav-prefs-head">
            <b>Settings</b>
            <button className="nav-prefs-close" onClick={() => setPrefs(false)} aria-label="Close settings">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="nav-prefs-body">
            <div className="nav-prefs-title">Appearance</div>
            <div className="nav-prefs-row">
              <span>Night mode</span>
              <div className="nav-seg">
                <button className={theme === 'dark' ? 'on' : ''} onClick={() => setTheme('dark')}>On</button>
                <button className={theme === 'light' ? 'on' : ''} onClick={() => setTheme('light')}>Off</button>
              </div>
            </div>

            <div className="nav-prefs-row">
              <span>Rotate with phone</span>
              <div className="nav-seg">
                <button
                  className={settings.compass ? 'on' : ''}
                  onClick={() => { update({ compass: true }); void startCompass(); }}
                  disabled={!compassSupported()}
                >
                  On
                </button>
                <button
                  className={!settings.compass ? 'on' : ''}
                  onClick={() => { update({ compass: false }); compassStop.current?.(); compassStop.current = null; }}
                >
                  Off
                </button>
              </div>
            </div>

            <div className="nav-prefs-title">Map</div>
            <div className="nav-prefs-row">
              <span>Mode</span>
              <div className="nav-seg">
                {(['road', 'satellite', 'hybrid'] as const).map((m) => (
                  <button
                    key={m}
                    className={settings.mapMode === m ? 'on' : ''}
                    onClick={() => update({ mapMode: m })}
                  >
                    {m === 'road' ? 'Road' : m === 'satellite' ? 'Satellite' : 'Hybrid'}
                  </button>
                ))}
              </div>
            </div>

            <div className="nav-prefs-title">Distances</div>
            <div className="nav-prefs-row">
              <span>Units</span>
              <div className="nav-seg">
                <button className={settings.unit === 'm' ? 'on' : ''} onClick={() => update({ unit: 'm' })}>Metres</button>
                <button className={settings.unit === 'yd' ? 'on' : ''} onClick={() => update({ unit: 'yd' })}>Yards</button>
              </div>
            </div>
            <div className="nav-prefs-row">
              <span>Tees</span>
              <div className="nav-seg">
                {teeOptions.map((t) => (
                  <button key={t} className={settings.teeColour === t ? 'on' : ''} onClick={() => update({ teeColour: t })}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="nav-prefs-title">Voice</div>
            <div className="nav-prefs-row">
              <span>Spoken directions</span>
              <div className="nav-seg">
                <button
                  className={!muted ? 'on' : ''}
                  onClick={() => { void unlockVoice(); setMuted(false); update({ voice: true }); }}
                  disabled={!voiceSupported()}
                >
                  On
                </button>
                <button
                  className={muted ? 'on' : ''}
                  onClick={() => { stopSpeaking(); setMuted(true); update({ voice: false }); }}
                >
                  Off
                </button>
              </div>
            </div>

            <div className="nav-prefs-foot">
              {BRAND.name} · {dir ? `${dir.count.toLocaleString()} courses` : 'loading'}
              {fix ? ` · GPS ±${Math.round(fix.accuracy)}m` : ' · waiting for GPS'}
              {widgetError() ? ` · TILE ERR ${widgetError()}` : ` · tile ${widgetPushCount()}`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const emptyFc = (): FeatureCollection => ({ type: 'FeatureCollection', features: [] });
const fc = (features: Feature[]): FeatureCollection => ({ type: 'FeatureCollection', features });
